import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { Chip } from '../../chip/Chip';
import { ChipLabelProps, Filter } from '../interface';
import { StyledPopper } from '../../menu/Popper/Popper';
import { LinkButton } from '../../link/LinkButton';
import { FilterList } from '../../list-conductor/list-actions';
import { FilterChipSection } from './FilterChipSection';
import { FilterChipsFactory, getFlatFiltersData } from './helper';
import { ChipSectionSeperator } from './ChipSectionSeperator';
import styles from './AppliedFiltersChips.module.scss';
import { Tooltip } from '../../tooltip/Tooltip';
import { useAnalyticsTracker } from '../../tracking/hooks/useAnalyticsTracker';
import { FilterTrackers } from '../filter-trackers';
import { ListConductor } from '../../list-conductor/list-conductor';
import { useResizeObserver } from '../../shared/hooks/useResizeObserver';
import { FilterContentFieldDataTypes } from '../constants';

const CHIP_MAX_WIDTH = 200;

interface AppliedFiltersChipsProps {
  listConductor: ListConductor;
  filtersMetaData: Filter[];
  entity: string;
}

export function AppliedFiltersChips({
  listConductor,
  filtersMetaData,
  entity
}: AppliedFiltersChipsProps) {
  let { filters } = listConductor.getListMeta();
  const { eventTrack } = useAnalyticsTracker();

  const [countToShow, setCountToShow] = useState<number>(1);
  const [showAllFilters, setShowAllFilters] = useState(false);
  const containerRef = useRef(null);

  const calcChipsToShow = () => {
    setCountToShow(
      Math.floor(
        ((containerRef.current as unknown as HTMLElement)?.offsetWidth || 0) / CHIP_MAX_WIDTH
      )
    );
  };

  useResizeObserver(calcChipsToShow, document.body);

  useLayoutEffect(() => {
    calcChipsToShow();
  }, []);

  filters = filters.filter(filter => {
    return filtersMetaData.findIndex(filterMeta => filterMeta.filterKey === filter.filter) > -1;
  });

  const chipsData = [...FilterChipsFactory(filters, filtersMetaData)];
  const flattenedChips = getFlatFiltersData(chipsData);
  const chipsToShow = flattenedChips.splice(0, countToShow || 1);

  useEffect(() => {
    if (flattenedChips.length === 0) {
      setShowAllFilters(false);
    }
  }, [flattenedChips.length]);

  const clearAllFilters = () => {
    listConductor.dispatch(new FilterList([]));
  };

  const clearFilter = (filterKey: string, filterValue: unknown) => {
    eventTrack({
      action: FilterTrackers.FILTER_CHIP_CLEAR_FILTER_CLICK,
      properties: {
        entity,
        filter: { filterKey, filterValue }
      }
    });

    const filterIndex = filters.findIndex(filter => filter.filter === filterKey);

    if (filterIndex > -1) {
      const filterValueAtIndex = filters[filterIndex].value;

      if (Array.isArray(filterValueAtIndex) && filters[filterIndex].operator !== 'RANGE') {
        const remainingValues = filterValueAtIndex.filter(
          value =>
            (typeof value === 'object' &&
              value.label !== (filterValue as { label: string }).label) ||
            value !== filterValue
        );

        filters[filterIndex].value = remainingValues;
      } else {
        filters[filterIndex].value = null;
      }

      listConductor.dispatch(new FilterList(filters));
    }
  };

  const clearFilterSection = (filterKey: string) => {
    eventTrack({
      action: FilterTrackers.FILTER_CHIP_CLEAR_FILTER_SECTION_CLICK,
      properties: {
        entity,
        filterKey
      }
    });

    const filterIndex = filters.findIndex(filter => filter.filter === filterKey);
    filters[filterIndex].value = [];
    listConductor.dispatch(new FilterList(filters));
  };

  const clearAllFiltersHandler = () => {
    eventTrack({
      action: FilterTrackers.FILTER_CHIP_CLEAR_ALL_FILTERS_CTA_CLICK,
      properties: {
        entity
      }
    });

    clearAllFilters();
  };

  return (
    <div
      data-id='applied-filters-chips'
      className='center-flex-row mx-3 flex-grow-1'
      ref={containerRef}
    >
      {chipsToShow.map((filter, index) => {
        const key = `${filter.title} ${index}`;
        return (
          <Chip
            className={styles.appliedFiltersChip}
            key={key}
            dataId={`${filter.title} ${index}`}
            size='large'
            label={<ChipLabel filter={filter} />}
            onDelete={() => {
              clearFilter(filter.filter, filter.value);
            }}
          />
        );
      })}

      {flattenedChips.length ? (
        <>
          <Chip
            id='show-more'
            size='large'
            dataId='show-more'
            label={`+${flattenedChips.length}`}
            onClick={() => {
              setShowAllFilters(true);
            }}
          />
          <StyledPopper
            target='#show-more'
            dataId='all-filters-popper'
            open={showAllFilters}
            style={{ maxWidth: '400px', minWidth: '400px' }}
            onClose={() => setShowAllFilters(false)}
          >
            <div className='py-3'>
              <div className='d-flex justify-between border-bottom px-3 py-2'>
                <p className='text-body-1 text-secondary'>Applied Filters</p>
                <LinkButton dataId='clear-all' onClick={() => clearAllFiltersHandler()}>
                  Clear all
                </LinkButton>
              </div>

              {chipsData.map((filter, index) => {
                return (
                  <>
                    <FilterChipSection
                      key={filter.filter}
                      filter={filter}
                      clearFilter={clearFilter}
                      clearSection={clearFilterSection}
                    />
                    {index < chipsData.length - 1 ? <ChipSectionSeperator /> : null}
                  </>
                );
              })}
            </div>
          </StyledPopper>
        </>
      ) : null}
    </div>
  );
}

function ChipLabel({ filter }: ChipLabelProps) {
  let label;
  if (filter.filterType === FilterContentFieldDataTypes.DATE) {
    label =
      `${filter.title} ` +
      `${'Between '} ` +
      `${dayjs((filter.value as Array<string>)[0]).format('DD-MM-YYYY')} And ${dayjs(
        (filter.value as Array<string>)[1]
      ).format('DD-MM-YYYY')}`;
  } else if (
    typeof filter.value === 'object' &&
    'value' in (filter.value as { label: string; value: string })
  ) {
    label = `${filter.title} : ${(filter.value as { label?: string }).label}`;
  } else {
    label = `${filter.title} ${filter.operator ?? ':'} ${filter.value as string}`;
  }

  return (
    <Tooltip enableOnTextOverflow title={label} dataId={filter.title}>
      <p className='text-ellipsis'>{label}</p>
    </Tooltip>
  );
}
